import { useState } from 'react';
import { reviews } from '@/mocks/content';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypotValue = (formData.get('phone_alt') as string || '').trim();
    if (honeypotValue) {
      setFormStatus('success');
      form.reset();
      return;
    }

    formData.delete('phone_alt');
    setFormStatus('submitting');
    setFormError('');

    try {
      const urlEncoded = new URLSearchParams();
      formData.forEach((value, key) => {
        urlEncoded.append(key, value.toString());
      });

      const response = await fetch('https://readdy.ai/api/form/d9u46bt3pcjqs2fcslo0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: urlEncoded.toString(),
      });

      const responseText = await response.text();
      let parsed: any = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // ignore parse errors
      }

      const isSpam = responseText.includes('spam') || responseText.includes('form data is spam');
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || '';

      if (response.ok && parsed?.code === 'OK' && !isSpam) {
        setFormStatus('success');
        form.reset();
      } else {
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后再试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查连接后重试');
    }
  };

  const values = [
    {
      title: '包容',
      description: '我们相信每个人都值得被尊重和接纳。无论你的背景、年龄、身份如何，男同网都是你温暖的归属。',
      icon: 'ri-heart-2-line',
    },
    {
      title: '真实',
      description: '鼓励真诚的自我表达和真实的交流。在这里，你可以卸下伪装，做最真实的自己。',
      icon: 'ri-user-smile-line',
    },
    {
      title: '互助',
      description: '社群的力量在于互相支持。我们倡导成员之间的互助精神，共同成长，共同面对挑战。',
      icon: 'ri-hand-heart-line',
    },
    {
      title: '成长',
      description: '提供有价值的知识和资源，帮助每一位成员在自我认同、健康管理和生活品质上不断进步。',
      icon: 'ri-seedling-line',
    },
  ];

  const milestones = [
    { year: '2020', event: '男同网正式成立，从一个小的线上社群开始起步' },
    { year: '2021', event: '上线健康专栏和权益支持板块，服务内容全面升级' },
    { year: '2022', event: '社群成员突破10,000人，开启线下活动组织' },
    { year: '2023', event: '发布真实故事专栏，收到超过500篇成员投稿' },
    { year: '2024', event: '覆盖全国30+城市，月活跃用户超过50,000人' },
    { year: '2025', event: '全新改版，优化用户体验，新增活动资讯板块' },
    { year: '2026', event: '持续成长，致力于为更多男同朋友提供温暖和支持' },
  ];

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于男同网',
            description: '男同网是专为男同群体打造的综合性平台，致力于提供交友、社群互动、生活资讯与权益支持服务。',
            url: 'https://mylifeguide.github.io/about',
            inLanguage: 'zh-CN',
            about: {
              '@type': 'Organization',
              name: '男同网',
              foundingDate: '2020',
              description: '男同网是专为男同群体打造的综合性平台。',
            },
          }),
        }}
      />

      <article className="min-h-screen bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6 pt-20 pb-16">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '关于我们' },
            ]}
          />

          <section className="mb-16 md:mb-20">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-3xl md:text-4xl font-bold font-heading text-foreground-900 mb-6">
                关于男同网
              </h1>
              <p className="text-base text-foreground-600 leading-relaxed mb-4">
                男同网成立于2020年，是一个专为男同群体打造的综合性线上平台。我们致力于创建一个安全、包容、多元的社区环境，让每一位成员都能在这里找到归属感、获取有价值的信息，并与志同道合的朋友建立真诚的连接。
              </p>
              <p className="text-base text-foreground-600 leading-relaxed">
                从最初的一个小型线上社群，到如今覆盖全国30多个城市、拥有超过50,000名成员的平台，我们始终坚守初心：为男同社群提供一个温暖的家。
              </p>
            </div>
          </section>

          <section className="mb-16 md:mb-20">
            <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 text-center mb-12">
              我们的价值观
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {values.map((v) => (
                <div
                  key={v.title}
                  className="p-6 rounded-xl bg-background-100 border border-background-200/70 text-center"
                >
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-4">
                    <i className={`${v.icon} text-xl`}></i>
                  </div>
                  <h3 className="text-lg font-semibold text-foreground-900 mb-2 font-heading">{v.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{v.description}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-16 md:mb-20">
            <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 text-center mb-12">
              发展历程
            </h2>
            <div className="max-w-2xl mx-auto">
              <div className="relative">
                <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300 md:-translate-x-px"></div>
                {milestones.map((m, index) => (
                  <div
                    key={m.year}
                    className={`relative flex items-start gap-6 mb-8 ${
                      index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                  >
                    <div className="hidden md:block w-1/2"></div>
                    <div className="absolute left-4 md:left-1/2 w-3 h-3 rounded-full bg-primary-500 border-4 border-background-50 -translate-x-1/2 mt-1.5"></div>
                    <div className="md:w-1/2 pl-10 md:pl-0 md:px-8">
                      <div className="bg-background-100 rounded-xl p-4 md:p-5 border border-background-200/70">
                        <span className="text-sm font-bold text-primary-600 font-heading">{m.year}</span>
                        <p className="text-sm text-foreground-600 mt-1 leading-relaxed">{m.event}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="mb-16 md:mb-20">
            <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 text-center mb-12">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {reviews.map((review) => (
                <div
                  key={review.id}
                  className="p-6 rounded-xl bg-background-100 border border-background-200/70"
                >
                  <div className="flex items-center gap-1 mb-3">
                    {[...Array(review.rating)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-amber-400 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">"{review.text}"</p>
                  <div className="flex items-center gap-3">
                    <img
                      src={review.avatar}
                      alt={review.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <div className="text-sm font-medium text-foreground-900">{review.name}</div>
                      <div className="text-xs text-foreground-400">{review.location}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="max-w-2xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 text-center mb-4">
              联系我们
            </h2>
            <p className="text-center text-foreground-500 text-sm mb-10">
              有任何问题、建议或合作意向，欢迎通过以下表单与我们联系
            </p>

            <form
              onSubmit={handleSubmit}
              data-readdy-form
              className="space-y-5"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    姓名 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                    placeholder="你的名字"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    邮箱 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                    placeholder="your@email.com"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  主题 <span className="text-primary-500">*</span>
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  required
                  className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                  placeholder="请选择主题"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  留言内容 <span className="text-primary-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                  placeholder="请输入你的留言内容（最多500字）"
                ></textarea>
                <p className="text-xs text-foreground-400 mt-1">最多 500 字</p>
              </div>

              <div className="hp-field">
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>

              <button
                type="submit"
                disabled={formStatus === 'submitting'}
                className="w-full py-3 rounded-lg bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                {formStatus === 'submitting' ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    提交中...
                  </span>
                ) : (
                  '提交留言'
                )}
              </button>

              {formStatus === 'success' && (
                <div className="p-4 rounded-lg bg-accent-100 text-accent-800 text-sm text-center">
                  <i className="ri-check-line mr-1.5"></i>
                  感谢你的留言！我们会尽快与你联系。
                </div>
              )}
              {formStatus === 'error' && (
                <div className="p-4 rounded-lg bg-red-50 text-red-600 text-sm text-center">
                  <i className="ri-error-warning-line mr-1.5"></i>
                  {formError}
                </div>
              )}
            </form>

            <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-5">
              <div className="text-center p-5 rounded-xl bg-background-100 border border-background-200/70">
                <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-3">
                  <i className="ri-mail-line text-lg"></i>
                </div>
                <h4 className="text-sm font-semibold text-foreground-900 mb-1">邮件联系</h4>
                <p className="text-xs text-foreground-500">contact@nantongwang.com</p>
              </div>
              <div className="text-center p-5 rounded-xl bg-background-100 border border-background-200/70">
                <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-3">
                  <i className="ri-wechat-line text-lg"></i>
                </div>
                <h4 className="text-sm font-semibold text-foreground-900 mb-1">微信公众号</h4>
                <p className="text-xs text-foreground-500">搜索"男同网"关注我们</p>
              </div>
              <div className="text-center p-5 rounded-xl bg-background-100 border border-background-200/70">
                <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 mb-3">
                  <i className="ri-question-answer-line text-lg"></i>
                </div>
                <h4 className="text-sm font-semibold text-foreground-900 mb-1">社群交流</h4>
                <p className="text-xs text-foreground-500">加入线上社群一起交流</p>
              </div>
            </div>
          </section>
        </div>
      </article>
    </>
  );
}